import apiClient from './client';
import {
  LoginRequest,
  LoginResponse,
  LoginApiResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  User,
  UpdateProfileRequest,
  ChangePasswordRequest,
  TwoFactorSetupResponse,
  TwoFactorSetupCompleteResponse,
  TwoFactorStatusResponse,
} from '../types';

export const authApi = {
  /**
   * Login with email and password
   * Returns LoginResponse on success, or TwoFactorRequiredResponse if 2FA is enabled
   */
  login: async (credentials: LoginRequest): Promise<LoginApiResponse> => {
    const response = await apiClient.post<LoginApiResponse>('/auth/login', {
      email: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  /**
   * Register a new user and organization
   */
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>('/auth/register', data);
    return response.data;
  },

  /**
   * Logout (invalidate refresh token)
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  /**
   * Refresh access token using refresh token cookie
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data;
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Update current user's profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    const response = await apiClient.put<User>('/auth/me', data);
    return response.data;
  },

  /**
   * Change current user's password
   */
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    await apiClient.post('/auth/change-password', data);
  },

  // ============== 2FA Methods ==============

  /**
   * Get current user's 2FA status
   */
  get2FAStatus: async (): Promise<TwoFactorStatusResponse> => {
    const response = await apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status');
    return response.data;
  },

  /**
   * Initiate 2FA setup - generates QR code and secret
   */
  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    const response = await apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup');
    return response.data;
  },

  /**
   * Complete 2FA setup by verifying TOTP code
   * Returns backup codes on success
   */
  verifySetup2FA: async (code: string): Promise<TwoFactorSetupCompleteResponse> => {
    const response = await apiClient.post<TwoFactorSetupCompleteResponse>(
      '/auth/2fa/verify-setup',
      { code }
    );
    return response.data;
  },

  /**
   * Regenerate backup codes (requires valid TOTP code)
   */
  regenerateBackupCodes: async (code: string): Promise<TwoFactorSetupCompleteResponse> => {
    const response = await apiClient.post<TwoFactorSetupCompleteResponse>(
      '/auth/2fa/regenerate-backup-codes',
      { code }
    );
    return response.data;
  },

  /**
   * Complete login with 2FA verification
   * Use when login returns requires_2fa: true
   */
  verifyLogin2FA: async (
    tempToken: string,
    code: string
  ): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/2fa/verify-login', {
      temp_token: tempToken,
      code,
    });
    return response.data;
  },

  // ============== Password Reset Methods ==============

  /**
   * Request password reset email
   * Always returns success (doesn't reveal if email exists)
   */
  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password using token from email
   */
  resetPassword: async (token: string, password: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, password });
  },
};
